@let pricingModels = pricingModels$ | async;
@let filteredFunctionKeys = getFunctionKeysFromModels(pricingModels ?? []);

<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Pricing Models</h1>
    <p class="!mt-0 text-muted-color">
      Manage pricing logic and model parameters.
    </p>
  </div>
  <p-button
    label="New Pricing Model"
    icon="pi pi-plus"
    (onClick)="openCreateDrawer()"
  />
</div>

<div class="flex flex-wrap gap-3 items-end justify-between mb-4">
  <div class="flex flex-wrap gap-2 items-center">
    <p-iconfield>
      <p-inputicon styleClass="pi pi-search" />
      <input
        pInputText
        [formControl]="searchQuery"
        placeholder="Search models…"
        class="w-56"
      />
    </p-iconfield>

    <p-multiselect
      [formControl]="selectedFunctionKeyFilters"
      [options]="functionKeyFilterOptions() || []"
      optionLabel="label"
      optionValue="value"
      placeholder="Function Key"
      [showToggleAll]="false"
      [maxSelectedLabels]="2"
      styleClass="w-52"
    />

    @if (hasActiveFilters) {
      <p-button
        label="Clear filters"
        icon="pi pi-times"
        severity="secondary"
        size="small"
        [text]="true"
        (onClick)="clearFilters()"
      />
    }
  </div>

  <div class="flex flex-wrap items-center gap-3">
    <div class="flex items-center gap-2">
      <p-checkbox
        [formControl]="includeInactive"
        [binary]="true"
        inputId="includeInactive"
      />
      <label for="includeInactive" class="text-sm font-medium"
        >Include Inactive Pricing Models</label
      >
    </div>

    <p-button
      [label]="
        expandedGroups.size === filteredFunctionKeys.length
          ? 'Collapse All'
          : 'Expand All'
      "
      [icon]="
        expandedGroups.size === filteredFunctionKeys.length
          ? 'pi pi-angle-double-up'
          : 'pi pi-angle-double-down'
      "
      severity="secondary"
      (onClick)="toggleAllGroups(filteredFunctionKeys)"
      [disabled]="filteredFunctionKeys.length === 0"
    />
  </div>
</div>

@if (pricingModels && pricingModels.length > 0) {
  <div class="rounded-xl border border-surface-switch-200 shadow-sm">
    <!-- Header Row -->
    <div
      class="hidden lg:grid rounded-t-xl bg-surface-switch-100 items-center gap-4 px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase border-b border-surface-switch-200"
      style="grid-template-columns: 2fr 3fr 4fr 1fr 1fr 1fr"
    >
      <div>Name</div>
      <div>Description</div>
      <div>Parameters</div>
      <div>Output Type</div>
      <div>Status</div>
      <div>Actions</div>
    </div>

    <div class="flex flex-col">
      @for (functionKey of filteredFunctionKeys; track functionKey) {
        @let groupModels = getModelsForFunctionKey(pricingModels, functionKey);

        <!-- Group Header -->
        <div
          class="border-b border-surface-switch-200 bg-surface-switch-100 px-4 py-3 flex items-center gap-3"
        >
          <p-button
            [text]="true"
            severity="secondary"
            [rounded]="true"
            size="small"
            [icon]="
              isGroupExpanded(functionKey)
                ? 'pi pi-chevron-down'
                : 'pi pi-chevron-right'
            "
            (onClick)="toggleGroup(functionKey)"
          />
          <span class="text-base font-semibold"
            >Core Model: {{ functionKey }}</span
          >
          <span
            class="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs"
            >{{
              groupModels.length +
                (groupModels.length === 1 ? " model" : " models")
            }}</span
          >
        </div>

        <!-- Group Rows -->
        @if (isGroupExpanded(functionKey)) {
          @for (model of groupModels; track model.id) {
            <!-- Mobile Layout -->
            <div
              class="lg:hidden border-b border-surface-switch-200 last:border-b-0 bg-surface-switch-50 p-4 flex flex-col gap-2 hover:bg-surface-switch-100 transition-colors duration-200"
            >
              <div class="flex justify-between items-start">
                <span class="font-medium">{{ model.name }}</span>
                <div class="flex gap-1 shrink-0 ml-2">
                  @if (model.is_active) {
                    <p-button
                      icon="pi pi-pencil"
                      severity="secondary"
                      size="small"
                      text="true"
                      (onClick)="openDrawer(model)"
                      pTooltip="Edit"
                      tooltipPosition="top"
                    />
                    <p-button
                      icon="pi pi-box"
                      severity="secondary"
                      size="small"
                      text="true"
                      (click)="openConfirm($event, model, 'archive')"
                      pTooltip="Archive"
                      tooltipPosition="top"
                    />
                  } @else {
                    <p-button
                      icon="pi pi-refresh"
                      severity="secondary"
                      size="small"
                      text="true"
                      (click)="openConfirm($event, model, 'unarchive')"
                      pTooltip="Unarchive"
                      tooltipPosition="top"
                    />
                  }
                </div>
              </div>
              @if (model.description) {
                <div class="text-sm text-muted-color">
                  {{ model.description }}
                </div>
              }
              <div class="flex gap-1 flex-wrap">
                @if (model.is_active) {
                  <p-chip label="Active" class="text-xs" />
                } @else {
                  <p-chip
                    label="Inactive"
                    class="text-xs"
                    severity="secondary"
                  />
                }
                @if (model.output_unit_cost) {
                  <p-chip
                    label="Unit Cost"
                    class="text-xs"
                    severity="success"
                  />
                }
                @if (model.output_quantity) {
                  <p-chip label="Quantity" class="text-xs" severity="success" />
                }
              </div>
            </div>

            <!-- Desktop Layout -->
            <div
              class="hidden lg:grid border-b border-surface-switch-200 last:border-b-0 bg-surface-switch-50 items-center gap-4 px-4 py-3 hover:bg-surface-switch-100 transition-colors duration-200"
              style="grid-template-columns: 2fr 3fr 4fr 1fr 1fr 1fr"
            >
              <div class="font-medium">{{ model.name }}</div>
              <div class="text-sm">
                @if (model.description) {
                  <span>{{ model.description }}</span>
                } @else {
                  <span class="text-muted-color">—</span>
                }
              </div>
              <div>
                @let allParams = getAllParametersForDisplay(model);
                @if (allParams.length > 0) {
                  <div class="flex flex-wrap gap-1 items-center">
                    @for (param of allParams; track param.name) {
                      @if (param.isSet) {
                        <p-chip
                          [label]="
                            param.label + ': ' + getParamValueDisplay(param)
                          "
                          [pTooltip]="param.schema.description"
                          tooltipPosition="top"
                          class="text-xs"
                        />
                      } @else {
                        <p-chip
                          [label]="
                            param.label + ': ' + getUnsetParamDisplay(param)
                          "
                          [pTooltip]="param.schema.description"
                          tooltipPosition="top"
                          class="text-xs"
                        />
                      }
                    }
                  </div>
                } @else {
                  <span class="text-muted-color">—</span>
                }
              </div>
              <div>
                <div class="flex flex-wrap gap-1">
                  @if (model.output_unit_cost) {
                    <p-chip
                      label="Unit Cost"
                      class="text-xs"
                      severity="success"
                    />
                  }
                  @if (model.output_quantity) {
                    <p-chip
                      label="Quantity"
                      class="text-xs"
                      severity="success"
                    />
                  }
                </div>
              </div>
              <div>
                @if (model.is_active) {
                  <p-chip label="Active" class="text-xs" />
                } @else {
                  <p-chip
                    label="Inactive"
                    class="text-xs"
                    severity="secondary"
                  />
                }
              </div>
              <div class="flex gap-1">
                @if (model.is_active) {
                  <p-button
                    icon="pi pi-pencil"
                    severity="secondary"
                    size="small"
                    text="true"
                    (onClick)="openDrawer(model)"
                    pTooltip="Edit"
                    tooltipPosition="top"
                  />
                  <p-button
                    icon="pi pi-box"
                    severity="secondary"
                    size="small"
                    text="true"
                    (click)="openConfirm($event, model, 'archive')"
                    pTooltip="Archive"
                    tooltipPosition="top"
                  />
                } @else {
                  <p-button
                    icon="pi pi-refresh"
                    severity="secondary"
                    size="small"
                    text="true"
                    (click)="openConfirm($event, model, 'unarchive')"
                    pTooltip="Unarchive"
                    tooltipPosition="top"
                  />
                }
              </div>
            </div>
          }
        }
      }
    </div>
  </div>
} @else if (hasActiveFilters) {
  <div class="text-center text-muted-color py-12">
    <i class="pi pi-filter-slash text-4xl mb-4 block"></i>
    <p class="mb-2">No pricing models match your filters.</p>
    <p-button
      label="Clear filters"
      icon="pi pi-times"
      severity="secondary"
      size="small"
      (onClick)="clearFilters()"
    />
  </div>
} @else {
  <div class="text-center text-muted-color">
    <p>No pricing models</p>
  </div>
}

<shared-confirm-dialog />

<p-drawer
  [(visible)]="isDrawerVisible"
  [header]="drawerHeader"
  position="right"
  [styleClass]="'!w-full lg:!w-3/4 2xl:!w-1/2'"
>
  <form [formGroup]="pricingModelFormGroup" class="flex flex-col gap-6">
    <!-- Name -->
    <div class="pt-1">
      <p-floatlabel variant="on">
        <input pInputText formControlName="name" type="text" class="w-full" />
        <label>Name</label>
      </p-floatlabel>
    </div>

    <!-- Function Key -->
    <div>
      <p-floatlabel variant="on">
        <p-select
          formControlName="function_key"
          [options]="functionKeyFilterOptions() || []"
          optionLabel="label"
          optionValue="value"
          class="w-full p-inputwrapper-filled"
          (onChange)="onFunctionKeyChange($event.value)"
        />
        <label>Function Key</label>
      </p-floatlabel>
    </div>

    <!-- Description -->
    <div>
      <p-floatlabel variant="on">
        <textarea
          pInputTextarea
          id="description"
          formControlName="description"
          class="w-full"
          [rows]="3"
          [class.p-filled]="pricingModelFormGroup.get('description')?.value"
        ></textarea>
        <label for="description">Description</label>
      </p-floatlabel>
    </div>

    <p-divider class="my-1!" />

    <!-- Parameters -->
    <div class="flex flex-col gap-4">
      <h4 class="text-lg font-semibold">Parameters</h4>
      @if (hasActiveParameterSchemas) {
        <app-parameter-editor
          [parameterSchemas]="activeParameterSchemas"
          [values]="editableParameterValues"
          idPrefix="pricing-model-"
          (parameterChanged)="onParameterEditorChange($event)"
        />
      } @else {
        <div class="text-muted-color text-sm">
          <p>No parameters defined for this pricing model.</p>
        </div>
      }
    </div>

    <!-- Footer with buttons -->
    <div class="flex justify-end gap-2 pt-4">
      <p-button label="Cancel" severity="secondary" (onClick)="closeDrawer()" />
      <p-button
        label="Save"
        icon="pi pi-save"
        (onClick)="savePricingModel()"
        [disabled]="
          (!pricingModelFormGroup.dirty && !parametersDirty) ||
          pricingModelFormGroup.invalid
        "
      />
    </div>
  </form>
</p-drawer>
